import React, { useState, useEffect } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import {
  FaFolder,
  FaFolderOpen,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaEyeSlash,
  FaSearch,
  FaSitemap,
  FaChevronRight,
  FaChevronDown,
  FaLink,
  FaLayerGroup,
} from "react-icons/fa";
import { toast } from "react-toastify";

export default function ModuleManagementPage() {
  const [modules, setModules] = useState([]);
  const [flatModules, setFlatModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubModuleModal, setIsSubModuleModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    title: "",
    code: "",
    icon: "FaFolder",
    routePath: "",
    moduleId: "",
    displayOrder: 0,
    isVisible: true,
    isActive: true,
  });

  const { token } = useAuth();

  const fetchModules = async () => {
    try {
      setLoading(true);
      const [treeRes, flatRes] = await Promise.all([
        axios.get(`${server}/modules`, {
          params: { search: searchTerm },
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${server}/modules/flat`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (treeRes.data.success) {
        setModules(treeRes.data.data || []);
      }
      if (flatRes.data.success) {
        setFlatModules(flatRes.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load module & submodule hierarchy");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      const timer = setTimeout(() => {
        fetchModules();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [token, searchTerm]);

  const handleOpenCreateModuleModal = () => {
    setEditingItem(null);
    setIsSubModuleModal(false);
    setFormData({
      title: "",
      code: "",
      icon: "FaFolder",
      routePath: "",
      moduleId: "",
      displayOrder: (modules.length + 1) * 10,
      isVisible: true,
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenCreateSubModuleModal = (parentModule = null) => {
    setEditingItem(null);
    setIsSubModuleModal(true);
    setFormData({
      title: "",
      code: "",
      icon: "FaFolder",
      routePath: "",
      moduleId: parentModule ? String(parentModule.id) : flatModules[0]?.id ? String(flatModules[0].id) : "",
      displayOrder: 10,
      isVisible: true,
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item, isSub) => {
    setEditingItem(item);
    setIsSubModuleModal(isSub);
    setFormData({
      title: item.title,
      code: item.code,
      icon: item.icon || "FaFolder",
      routePath: item.routePath || "",
      moduleId: isSub ? String(item.moduleId || "") : "",
      displayOrder: item.displayOrder || 0,
      isVisible: item.isVisible !== false,
      isActive: item.isActive !== false,
    });
    setIsModalOpen(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.code.trim()) {
      toast.error("Title and unique system code are required");
      return;
    }

    try {
      if (isSubModuleModal) {
        if (!formData.moduleId) {
          toast.error("Parent Module is required for SubModule");
          return;
        }
        const payload = {
          moduleId: parseInt(formData.moduleId, 10),
          title: formData.title.trim(),
          code: formData.code.trim().toUpperCase().replace(/\s+/g, "_"),
          icon: formData.icon.trim() || "FaFolder",
          routePath: formData.routePath.trim(),
          displayOrder: parseInt(formData.displayOrder, 10) || 0,
          isVisible: formData.isVisible,
          isActive: formData.isActive,
        };

        if (editingItem) {
          const res = await axios.put(`${server}/submodules/update?id=${editingItem.id}`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.success) toast.success("SubModule updated successfully");
        } else {
          const res = await axios.post(`${server}/submodules/create`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.success) toast.success("SubModule created successfully");
        }
      } else {
        const payload = {
          title: formData.title.trim(),
          code: formData.code.trim().toUpperCase().replace(/\s+/g, "_"),
          icon: formData.icon.trim() || "FaFolder",
          displayOrder: parseInt(formData.displayOrder, 10) || 0,
          isVisible: formData.isVisible,
          isActive: formData.isActive,
        };

        if (editingItem) {
          const res = await axios.put(`${server}/modules/update?id=${editingItem.id}`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.success) toast.success("Module updated successfully");
        } else {
          const res = await axios.post(`${server}/modules/create`, payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.success) toast.success("New Module created successfully");
        }
      }

      setIsModalOpen(false);
      fetchModules();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save changes");
    }
  };

  const handleToggleVisibility = async (item, isSub) => {
    try {
      const endpoint = isSub ? `${server}/submodules/toggleVisibility?id=${item.id}` : `${server}/modules/toggleVisibility?id=${item.id}`;
      const res = await axios.put(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toast.info(`'${item.title}' visibility set to ${res.data.isVisible ? "Visible" : "Hidden"}`);
        fetchModules();
      }
    } catch (error) {
      toast.error("Failed to toggle visibility");
    }
  };

  const handleDeleteItem = async (item, isSub) => {
    const typeLabel = isSub ? "SubModule" : "Module and all its SubModules";
    if (window.confirm(`Are you sure you want to delete ${typeLabel} '${item.title}'?`)) {
      try {
        const endpoint = isSub ? `${server}/submodules/deleteById?id=${item.id}` : `${server}/modules/deleteById?id=${item.id}`;
        const res = await axios.delete(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          toast.success(`${isSub ? "SubModule" : "Module"} deleted successfully`);
          fetchModules();
        }
      } catch (error) {
        toast.error("Failed to delete item");
      }
    }
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3.5 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl shadow-xs">
            <FaSitemap size={20} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
              Module Access Management (2-Tier RBAC)
            </h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">
              Manage parent Modules and child SubModules linked via Foreign Key
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleOpenCreateModuleModal}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-3.5 py-2 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5 shrink-0"
          >
            <FaPlus size={11} /> Create Module
          </button>
          <button
            onClick={() => handleOpenCreateSubModuleModal(null)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-xl font-black text-xs transition shadow-xs flex items-center gap-1.5 shrink-0"
          >
            <FaPlus size={11} /> Create SubModule
          </button>
        </div>
      </div>

      {/* Toolbar & Search */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-4 mb-4 shadow-xs flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="relative w-full sm:w-80">
          <FaSearch className="absolute left-3.5 top-3 text-gray-400 text-xs" />
          <input
            type="text"
            placeholder="Search modules & submodules..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs font-bold text-gray-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400 font-bold">
          <span className="flex items-center gap-1.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 px-3 py-1.5 rounded-xl border border-indigo-100 dark:border-indigo-900">
            <FaLayerGroup size={12} /> Modules: {modules.length}
          </span>
        </div>
      </div>

      {/* Modules & SubModules Tree List */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl p-5 shadow-xs mb-8">
        {loading ? (
          <div className="py-20 text-center text-xs font-bold text-indigo-600">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-3"></div>
            Loading modules & submodules...
          </div>
        ) : modules.length === 0 ? (
          <div className="py-16 text-center text-xs font-semibold text-gray-400 italic">
            No system modules defined yet. Click "Create Module" to start building.
          </div>
        ) : (
          <div className="space-y-4">
            {modules.map((mod) => (
              <ModuleCard
                key={mod.id}
                module={mod}
                onAddSubModule={handleOpenCreateSubModuleModal}
                onEditModule={(m) => handleOpenEditModal(m, false)}
                onEditSubModule={(s) => handleOpenEditModal(s, true)}
                onToggleVisibility={handleToggleVisibility}
                onDeleteItem={handleDeleteItem}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 w-full max-w-lg rounded-2xl shadow-2xl p-6 relative animate-in fade-in zoom-in duration-150">
            <div className="flex justify-between items-center pb-3 border-b border-gray-100 dark:border-slate-800 mb-4">
              <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
                <FaSitemap className="text-indigo-600 dark:text-indigo-400" />
                {editingItem
                  ? isSubModuleModal
                    ? "Edit SubModule"
                    : "Edit Module"
                  : isSubModuleModal
                  ? "Create SubModule"
                  : "Create Module"}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 text-base font-bold"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-4 text-xs font-bold">
              {isSubModuleModal && (
                <div>
                  <label className="block text-[10px] font-black uppercase text-gray-500 dark:text-slate-400 mb-1">
                    Parent Module * (Foreign Key)
                  </label>
                  <select
                    required
                    value={formData.moduleId}
                    onChange={(e) => setFormData({ ...formData, moduleId: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select Parent Module...</option>
                    {flatModules.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.title} ({m.code})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-[10px] font-black uppercase text-gray-500 dark:text-slate-400 mb-1">
                  {isSubModuleModal ? "SubModule Title *" : "Module Title *"}
                </label>
                <input
                  type="text"
                  required
                  placeholder={isSubModuleModal ? "e.g. Departments" : "e.g. Academics"}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-black uppercase text-gray-500 dark:text-slate-400 mb-1">
                    System Code * (Unique Key)
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={isSubModuleModal ? "e.g. DEPARTMENTS" : "e.g. ACADEMICS"}
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-[11px]"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-black uppercase text-gray-500 dark:text-slate-400 mb-1">
                    React Icon Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. FaBuilding"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              {isSubModuleModal && (
                <div>
                  <label className="block text-[10px] font-black uppercase text-gray-500 dark:text-slate-400 mb-1">
                    Route Path (URL)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. departments or students/register"
                    value={formData.routePath}
                    onChange={(e) => setFormData({ ...formData, routePath: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-[11px]"
                  />
                </div>
              )}

              <div>
                <label className="block text-[10px] font-black uppercase text-gray-500 dark:text-slate-400 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isVisible}
                    onChange={(e) => setFormData({ ...formData, isVisible: e.target.checked })}
                    className="w-4 h-4 accent-indigo-600 rounded cursor-pointer"
                  />
                  <span>Visible in Navigation</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-4 h-4 accent-indigo-600 rounded cursor-pointer"
                  />
                  <span>Is Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-gray-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-black transition shadow-xs"
                >
                  {editingItem ? "Save Changes" : isSubModuleModal ? "Create SubModule" : "Create Module"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function ModuleCard({ module, onAddSubModule, onEditModule, onEditSubModule, onToggleVisibility, onDeleteItem }) {
  const [isOpen, setIsOpen] = useState(true);
  const hasSubModules = module.subModules && module.subModules.length > 0;

  return (
    <div className="border border-gray-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-2xs">
      {/* Module Header Row */}
      <div className="bg-slate-100/80 dark:bg-slate-800/80 p-3.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-gray-200 dark:border-slate-700/60">
        <div className="flex items-center gap-3">
          {hasSubModules ? (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-1 text-gray-500 hover:text-indigo-600 transition"
            >
              {isOpen ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
            </button>
          ) : (
            <span className="w-3" />
          )}

          <div className="p-2 bg-indigo-100 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl">
            <FaFolderOpen size={14} />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-black text-gray-900 dark:text-white">{module.title}</span>
              <span className="font-mono text-[9px] bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded-md font-bold border border-indigo-200 dark:border-indigo-900">
                MODULE: {module.code}
              </span>
              {!module.isVisible && (
                <span className="text-[9px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-bold">Hidden</span>
              )}
            </div>
            <div className="text-[10px] text-gray-500 dark:text-slate-400 font-semibold mt-0.5">
              SubModules count: {module.subModules ? module.subModules.length : 0} • Display Order: {module.displayOrder}
            </div>
          </div>
        </div>

        {/* Module Actions */}
        <div className="flex items-center gap-2 self-end sm:self-center">
          <button
            onClick={() => onAddSubModule(module)}
            className="px-2.5 py-1 text-[11px] bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900 rounded-lg hover:bg-emerald-100 transition font-black flex items-center gap-1"
          >
            <FaPlus size={10} /> Add SubModule
          </button>
          <button
            onClick={() => onToggleVisibility(module, false)}
            className="p-1.5 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300 rounded-lg border border-gray-200 dark:border-slate-600 hover:bg-gray-50 transition"
            title={module.isVisible ? "Hide Module" : "Show Module"}
          >
            {module.isVisible ? <FaEye size={12} /> : <FaEyeSlash size={12} />}
          </button>
          <button
            onClick={() => onEditModule(module)}
            className="p-1.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-lg border border-indigo-200 dark:border-indigo-900 hover:bg-indigo-100 transition"
            title="Edit Module"
          >
            <FaEdit size={12} />
          </button>
          <button
            onClick={() => onDeleteItem(module, false)}
            className="p-1.5 bg-rose-50 dark:bg-rose-950 text-rose-600 dark:text-rose-400 rounded-lg border border-rose-200 dark:border-rose-900 hover:bg-rose-100 transition"
            title="Delete Module"
          >
            <FaTrash size={12} />
          </button>
        </div>
      </div>

      {/* SubModules Table / List */}
      {isOpen && (
        <div className="p-3 bg-white dark:bg-slate-900">
          {!hasSubModules ? (
            <div className="py-4 text-center text-xs text-gray-400 italic">
              No SubModules under '{module.title}'. Click "Add SubModule" to create one.
            </div>
          ) : (
            <div className="space-y-1.5">
              {module.subModules.map((sub) => (
                <div
                  key={sub.id}
                  className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-slate-800/50 rounded-xl border border-gray-100 dark:border-slate-800/80 hover:bg-gray-100/70 dark:hover:bg-slate-800 transition"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-gray-400 font-mono text-xs ml-2">└─</span>
                    <div className="p-1 bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300 rounded-lg">
                      <FaFolder size={11} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-800 dark:text-slate-200">{sub.title}</span>
                        <span className="font-mono text-[9px] bg-slate-200 dark:bg-slate-700 text-gray-700 dark:text-slate-300 px-1.5 py-0.5 rounded font-semibold">
                          {sub.code}
                        </span>
                        {!sub.isVisible && (
                          <span className="text-[9px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded font-bold">Hidden</span>
                        )}
                      </div>
                      {sub.routePath && (
                        <div className="text-[10px] text-gray-500 dark:text-slate-400 font-mono flex items-center gap-1 mt-0.5">
                          <FaLink size={9} /> Path: {sub.routePath}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] text-gray-400 font-bold mr-2">Order: {sub.displayOrder}</span>
                    <button
                      onClick={() => onToggleVisibility(sub, true)}
                      className="p-1.5 text-gray-500 hover:text-amber-600 transition"
                      title={sub.isVisible ? "Hide SubModule" : "Show SubModule"}
                    >
                      {sub.isVisible ? <FaEye size={11} /> : <FaEyeSlash size={11} />}
                    </button>
                    <button
                      onClick={() => onEditSubModule(sub)}
                      className="p-1.5 text-indigo-600 hover:text-indigo-800 transition"
                      title="Edit SubModule"
                    >
                      <FaEdit size={11} />
                    </button>
                    <button
                      onClick={() => onDeleteItem(sub, true)}
                      className="p-1.5 text-rose-600 hover:text-rose-800 transition"
                      title="Delete SubModule"
                    >
                      <FaTrash size={11} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
